import { createContext, useCallback, useContext, useEffect, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { Book } from "../api";
import type { ChapterRef, Rich } from "./types";

// -- Bookmark: in the URL (?bm=, explicit when shared) and remembered per reader -------------

const storageKey = (bookId: string) => `fabula.bookmark.${bookId}`;

function stored(bookId: string): number | null {
  try {
    const v = localStorage.getItem(storageKey(bookId));
    return v === null ? null : Number(v);
  } catch {
    return null;
  }
}

export function useBookmark(book: Book | null): [number, (bm: number) => void] {
  const [params, setParams] = useSearchParams();
  const first = book?.first_chapter_index ?? 0;
  const last = book?.last_chapter_index ?? 0;
  const fromUrl = params.get("bm");
  const raw = fromUrl !== null ? Number(fromUrl) : book ? (stored(book.id) ?? first) : 0;
  const bookmark = Math.min(Math.max(Number.isFinite(raw) ? raw : first, first), last);

  const setBookmark = useCallback(
    (bm: number) => {
      const next = new URLSearchParams(params);
      next.set("bm", String(bm));
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  useEffect(() => {
    if (!book) return;
    try {
      localStorage.setItem(storageKey(book.id), String(bookmark));
    } catch {
      /* per-reader convenience only */
    }
  }, [book, bookmark]);

  return [bookmark, setBookmark];
}

// -- Wiki context and links -------------------------------------------------------------------

export interface WikiCtx {
  book: Book;
  bookmark: number;
  isOwner: boolean;
}

export const WikiContext = createContext<WikiCtx | null>(null);

export function useWiki(): WikiCtx {
  const ctx = useContext(WikiContext);
  if (!ctx) throw new Error("useWiki outside WikiContext");
  return ctx;
}

export function wikiHref(bookId: string, sub: string, bookmark: number, extra = ""): string {
  return `/books/${bookId}/wiki${sub}?bm=${bookmark}${extra}`;
}

export function WLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  const { book, bookmark } = useWiki();
  return (
    <Link className={className} to={wikiHref(book.id, to, bookmark)}>
      {children}
    </Link>
  );
}

export function EntityLink({ id, name }: { id: number; name: string }) {
  return <WLink to={`/a/${id}`}>{name}</WLink>;
}

export function ChapterLink({ chapter, short = false }: { chapter: ChapterRef; short?: boolean }) {
  return (
    <WLink to={`/h/${chapter.index}`} className="chapter-tag-link">
      <span className="chapter-tag" title={chapter.title}>
        H{chapter.index}
      </span>
      {!short && <> {chapter.title}</>}
    </WLink>
  );
}

export function ChapterNo({ index }: { index: number }) {
  return (
    <WLink to={`/h/${index}`} className="chapter-tag-link">
      <span className="chapter-tag">H{index}</span>
    </WLink>
  );
}

export function RichText({ value }: { value: Rich | null | undefined }) {
  if (!value) return null;
  return (
    <>
      {value.map((s, i) =>
        s.e !== undefined ? <EntityLink key={i} id={s.e} name={s.t} /> : <span key={i}>{s.t}</span>,
      )}
    </>
  );
}

export function Footnote({ n }: { n: number }) {
  return (
    <sup className="fn">
      <a href={`#bron-${n}`} onClick={(e) => {
        e.preventDefault();
        document.getElementById(`bron-${n}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      }}>
        [{n}]
      </a>
    </sup>
  );
}
